import { apiFetch, API_BASE, setStoredToken, authHeader } from './client';
import type { AuthUser } from '../types/contracts';

export interface TelegramLoginPayload {
  id: number;
  first_name?: string;
  last_name?: string;
  username?: string;
  photo_url?: string;
  auth_date: number;
  hash: string;
}

export interface MeResponse {
  userId: number;
  authSource: 'tma' | 'web' | 'dev';
  profile: AuthUser;
}

export const authApi = {
  /** Вход через Telegram Login Widget. */
  loginWithTelegram: async (payload: TelegramLoginPayload): Promise<MeResponse['profile']> => {
    const res = await fetch(`${API_BASE}/api/auth/telegram`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify(payload),
    });
    if (!res.ok) {
      const txt = await res.text().catch(() => res.statusText);
      throw new Error(`Login failed: ${txt}`);
    }
    const data = (await res.json()) as { ok: boolean; token?: string; user: AuthUser };
    // Сохраняем token как fallback на случай, если cookie не применилась (cross-site).
    if (data.token) setStoredToken(data.token);
    return data.user;
  },

  /** Для dev/отладки: вход без виджета (работает только при пустом/'test' токене бота). */
  devLogin: async (id = 1): Promise<AuthUser> => {
    const res = await fetch(`${API_BASE}/api/auth/telegram`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify({ id, first_name: 'Dev', username: 'dev', auth_date: Math.floor(Date.now() / 1000), hash: 'dev' }),
    });
    if (!res.ok) throw new Error('dev login unavailable');
    const data = (await res.json()) as { token?: string; user: AuthUser };
    if (data.token) setStoredToken(data.token);
    return data.user;
  },

  /** Shag 1: nachat vhod cherez bota, poluchit token dlya deep-link. */
  startLogin: async (): Promise<{ token: string; expiresInSec: number }> => {
    const res = await fetch(`${API_BASE}/api/auth/telegram/login/start`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: '{}',
    });
    if (!res.ok) throw new Error('Не удалось начать вход');
    return (await res.json()) as { token: string; expiresInSec: number };
  },

  /** Shag 2: oprashivaem status; kogda bot podtverdil — vozvrashaem polzovatelya. */
  pollLogin: async (token: string): Promise<AuthUser | null> => {
    const res = await fetch(`${API_BASE}/api/auth/telegram/login/status?token=${encodeURIComponent(token)}`, {
      credentials: 'include',
    });
    if (!res.ok) return null;
    const data = (await res.json()) as { status: string; token?: string; user?: AuthUser };
    if (data.status === 'authorized' && data.user) {
      if (data.token) setStoredToken(data.token);
      return data.user;
    }
    if (data.status === 'expired') throw new Error('expired');
    return null;
  },

  /** Alternativa: vhod po odnorazovomu kodu iz bota (/login). */
  loginWithCode: async (code: string): Promise<AuthUser> => {
    const res = await fetch(`${API_BASE}/api/auth/telegram/login/code`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify({ code }),
    });
    if (!res.ok) {
      const txt = await res.text().catch(() => res.statusText);
      let msg = 'Не удалось войти по коду';
      try { msg = (JSON.parse(txt) as { error?: string }).error || msg; } catch { /* ignore */ }
      throw new Error(msg);
    }
    const data = (await res.json()) as { token?: string; user: AuthUser };
    if (data.token) setStoredToken(data.token);
    return data.user;
  },

  me: () => apiFetch<MeResponse>('/api/auth/me'),

  logout: async (): Promise<void> => {
    try {
      await fetch(`${API_BASE}/api/auth/logout`, {
        method: 'POST',
        credentials: 'include',
        headers: { ...authHeader() },
      });
    } finally {
      setStoredToken(null);
    }
  },
};
